import { zodResolver } from '@hookform/resolvers/zod';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import {
  RECORD_STATUSES,
  TICKET_STATUSES,
  ticketCommentSchema,
  ticketGroupInputSchema,
  ticketInputSchema,
  ticketTypeInputSchema,
} from '@app/shared';
import type {
  RecordStatus,
  TicketAssignKind,
  TicketAssignees,
  TicketDto,
  TicketGroupDto,
  TicketStatus,
  TicketTypeDto,
} from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { ErrorState, LoadingState } from '../../components/PageState';
import { StatusChip } from '../../components/StatusChip';
import { FormDialog } from '../../components/dialogs';
import {
  MultiSelectInput,
  SelectInput,
  TextInput,
  applyServerErrors,
  toOptions,
} from '../../components/form';
import type { Option } from '../../components/form';
import { formatDate, formatDateTime } from '../../lib/format';
import { withRequiredMessages } from '../inventory/formUtils';
import {
  useAddComment,
  useAssignees,
  useCreateTicket,
  useCreateTicketGroup,
  useCreateTicketType,
  useTicket,
  useTicketGroups,
  useTicketTypeOptions,
  useTicketTypes,
  useUpdateTicket,
  useUpdateTicketGroup,
  useUpdateTicketType,
} from './api';

const STATUS_OPTIONS = toOptions(TICKET_STATUSES);

const typeOptions = (types: Array<{ id: string; name: string }> | undefined): Option[] =>
  (types ?? []).map((t) => ({ value: t.id, label: t.name }));

/* ------------------------------ Add / Edit Ticket -------------------------- */

interface TicketForm {
  title: string;
  typeId: string;
  description: string;
  customerId: string;
  customerName: string;
  contactNumber: string;
  alternateNumber: string;
  status: TicketStatus | '';
  assignTo: TicketAssignKind;
  assigneeId: string;
}

const EMPTY_TICKET: TicketForm = {
  title: '',
  typeId: '',
  description: '',
  customerId: '',
  customerName: '',
  contactNumber: '',
  alternateNumber: '',
  status: '',
  assignTo: 'user',
  assigneeId: '',
};

const ticketResolver = withRequiredMessages<TicketForm>(
  zodResolver(ticketInputSchema) as unknown as Resolver<TicketForm>,
  {
    typeId: 'Select Ticket Type',
    status: 'Select Ticket Status',
    assigneeId: 'Select from the User List',
  },
);

/** "Add Ticket" and "Edit Ticket". `ticketId` set means edit. */
export function TicketFormDialog(props: {
  open: boolean;
  ticketId?: string;
  /** Pre-filled when a ticket is raised from a customer. */
  customer?: { customerId: number; name: string };
  onClose: () => void;
}) {
  const { open, ticketId, customer, onClose } = props;
  const notify = useNotify();
  const types = useTicketTypeOptions(open);
  const assignees = useAssignees(open);
  const existing = useTicket(open ? ticketId : undefined);
  const createTicket = useCreateTicket();
  const updateTicket = useUpdateTicket();
  const { control, handleSubmit, reset, setError, setValue, watch } = useForm<TicketForm>({
    defaultValues: EMPTY_TICKET,
    resolver: ticketResolver,
  });
  const assignTo = watch('assignTo');

  useEffect(() => {
    if (!open) return;
    const t = existing.data;
    reset(
      ticketId && t
        ? {
            title: t.title,
            typeId: t.typeId,
            description: t.description,
            customerId: String(t.customer.customerId),
            customerName: t.customer.name,
            contactNumber: t.contactNumber,
            alternateNumber: t.alternateNumber,
            status: t.status,
            assignTo: t.assignTo,
            assigneeId: t.assigneeId,
          }
        : {
            ...EMPTY_TICKET,
            customerId: customer ? String(customer.customerId) : '',
            customerName: customer?.name ?? '',
          },
    );
  }, [open, ticketId, existing.data, customer, reset]);

  const submit = handleSubmit(async (values) => {
    // The resolver hands over the parsed schema output, which is what the API expects.
    const input = values as unknown as Parameters<typeof createTicket.mutateAsync>[0];
    try {
      if (ticketId) {
        await updateTicket.mutateAsync({ id: ticketId, input });
        notify.success('Ticket updated.');
      } else {
        const created = await createTicket.mutateAsync(input);
        notify.success(`Ticket ${created.ticketNo} added.`);
      }
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  const list: TicketAssignees = assignees.data ?? { users: [], groups: [] };
  const assigneeOptions: Option[] =
    assignTo === 'group'
      ? list.groups.map((g) => ({ value: g.id, label: g.name }))
      : list.users.map((u) => ({ value: u.id, label: u.loginId }));

  return (
    <FormDialog
      open={open}
      title={ticketId ? 'Edit Ticket' : 'Add Ticket'}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={createTicket.isPending || updateTicket.isPending}
    >
      {ticketId && existing.isPending ? (
        <LoadingState />
      ) : ticketId && existing.error ? (
        <ErrorState error={existing.error} />
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <TextInput
              control={control}
              name="title"
              label="Title"
              required
              autoFocus
              slotProps={{ htmlInput: { maxLength: 255 } }}
            />
          </Box>
          <SelectInput
            control={control}
            name="typeId"
            label="Ticket Type"
            required
            emptyLabel="Select Ticket Type"
            options={typeOptions(types.data)}
          />
          <SelectInput
            control={control}
            name="status"
            label="Status"
            required
            emptyLabel="Select Ticket Status"
            options={STATUS_OPTIONS}
          />
          <Box sx={{ gridColumn: '1 / -1' }}>
            <TextInput
              control={control}
              name="description"
              label="Description"
              required
              multiline
              minRows={3}
              slotProps={{ htmlInput: { maxLength: 2000 } }}
            />
          </Box>
          <TextInput
            control={control}
            name="customerId"
            label="Customer ID"
            required
            slotProps={{ htmlInput: { maxLength: 11, inputMode: 'numeric' } }}
          />
          <TextInput
            control={control}
            name="customerName"
            label="Customer Name"
            required
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />
          <TextInput
            control={control}
            name="contactNumber"
            label="Contact Number"
            slotProps={{ htmlInput: { maxLength: 10, inputMode: 'numeric' } }}
          />
          <TextInput
            control={control}
            name="alternateNumber"
            label="Alternate Number"
            slotProps={{ htmlInput: { maxLength: 10, inputMode: 'numeric' } }}
          />
          <Controller
            control={control}
            name="assignTo"
            render={({ field }) => (
              <FormControl>
                <FormLabel id="assign-ticket-to" sx={{ fontSize: 13 }}>
                  Assign Ticket To *
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="assign-ticket-to"
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    // The other list has other entries.
                    setValue('assigneeId', '');
                  }}
                >
                  <FormControlLabel value="user" control={<Radio size="small" />} label="User" />
                  <FormControlLabel value="group" control={<Radio size="small" />} label="Group" />
                </RadioGroup>
              </FormControl>
            )}
          />
          <SelectInput
            control={control}
            name="assigneeId"
            label="User List"
            required
            emptyLabel="Select"
            options={assigneeOptions}
          />
        </Box>
      )}
    </FormDialog>
  );
}

/* -------------------------------- Show Ticket ------------------------------ */

interface CommentForm {
  comment: string;
  /** `user:<id>` or `group:<id>`. */
  assignee: string;
  status: TicketStatus;
  typeId: string;
}

function CommentBox({ ticket, onPosted }: { ticket: TicketDto; onPosted: () => void }) {
  const notify = useNotify();
  const types = useTicketTypeOptions();
  const assignees = useAssignees(true);
  const addComment = useAddComment();
  const { control, handleSubmit, setError, reset } = useForm<CommentForm>({
    defaultValues: {
      comment: '',
      assignee: `${ticket.assignTo}:${ticket.assigneeId}`,
      status: ticket.status,
      typeId: ticket.typeId,
    },
  });

  const submit = handleSubmit(async (values) => {
    const [assignTo, assigneeId] = values.assignee.split(':') as [TicketAssignKind, string];
    const parsed = ticketCommentSchema.safeParse({
      comment: values.comment,
      assignTo,
      assigneeId,
      status: values.status,
      typeId: values.typeId,
    });
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] === 'assigneeId' ? 'assignee' : (issue.path[0] as string);
        setError(field as keyof CommentForm, { type: 'validate', message: issue.message });
      }
      return;
    }
    try {
      const updated = await addComment.mutateAsync({ id: ticket.id, input: parsed.data });
      notify.success('Comment posted.');
      reset({
        comment: '',
        assignee: `${updated.assignTo}:${updated.assigneeId}`,
        status: updated.status,
        typeId: updated.typeId,
      });
      onPosted();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  const list = assignees.data ?? { users: [], groups: [] };
  const assigneeOptions: Option[] = [
    ...list.users.map((u) => ({ value: `user:${u.id}`, label: u.loginId })),
    ...list.groups.map((g) => ({ value: `group:${g.id}`, label: `${g.name} (Group)` })),
  ];
  // A type made inactive since stays selectable on the ticket that has it.
  const typeChoices = typeOptions(types.data);
  if (!typeChoices.some((t) => t.value === ticket.typeId))
    typeChoices.push({ value: ticket.typeId, label: ticket.type });

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(e) => void submit(e)}
      aria-label="Post comment"
      sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}
    >
      <Box sx={{ gridColumn: '1 / -1' }}>
        <TextInput
          control={control}
          name="comment"
          label="Comment"
          required
          multiline
          minRows={3}
          slotProps={{ htmlInput: { maxLength: 2000 } }}
        />
      </Box>
      <SelectInput
        control={control}
        name="assignee"
        label="Assign To"
        required
        options={assigneeOptions}
      />
      <SelectInput
        control={control}
        name="status"
        label="Status"
        required
        options={STATUS_OPTIONS}
      />
      <SelectInput control={control} name="typeId" label="Type" required options={typeChoices} />
      <Box>
        <Button type="submit" variant="contained" disabled={addComment.isPending}>
          Post comment
        </Button>
      </Box>
    </Box>
  );
}

/** "Show Ticket": the ticket, its comment thread and the comment form. */
export function ShowTicketDialog({
  ticketId,
  onClose,
}: {
  ticketId?: string;
  onClose: () => void;
}) {
  const ticket = useTicket(ticketId);
  const t = ticket.data;
  return (
    <Dialog open={Boolean(ticketId)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t ? `${t.ticketNo} : ${t.title}` : 'Ticket'}</DialogTitle>
      <DialogContent dividers>
        {ticket.error ? (
          <ErrorState error={ticket.error} onRetry={() => void ticket.refetch()} />
        ) : !t ? (
          <LoadingState />
        ) : (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Typography>
                <strong>Last Updated:</strong> {formatDateTime(t.updatedAt)}
              </Typography>
              <Typography>
                <strong>Last Commented:</strong> {formatDateTime(t.lastCommentedAt) || '-'}
              </Typography>
              <Typography>
                <strong>Created Date:</strong> {formatDateTime(t.createdAt)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography>
                <strong>Customer:</strong> {t.customer.name} ({t.customer.customerId})
              </Typography>
              <Typography>
                <strong>Assigned To:</strong> {t.assignedTo}
              </Typography>
              <StatusChip status={t.status} />
            </Box>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{t.description}</Typography>
            <Divider />
            <Box component="section" aria-label="Comments" sx={{ display: 'grid', gap: 1.5 }}>
              {t.comments.length === 0 ? (
                <Typography color="text.secondary">No comments yet.</Typography>
              ) : (
                t.comments.map((c) => (
                  <Box key={c.id}>
                    <Typography variant="caption" color="text.secondary">
                      {c.by?.loginId ?? ''} · {formatDateTime(c.at)}
                    </Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{c.text}</Typography>
                    {c.changes.map((change) => (
                      <Typography key={change} variant="caption" sx={{ display: 'block' }}>
                        {change}
                      </Typography>
                    ))}
                  </Box>
                ))
              )}
            </Box>
            <Divider />
            {/* `key` restarts the form from the ticket's new assignee, status and type. */}
            <CommentBox key={t.updatedAt} ticket={t} onPosted={() => void ticket.refetch()} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/* -------------------------------- Ticket Type ------------------------------ */

interface TypeForm {
  name: string;
  status: RecordStatus | '';
}
const EMPTY_TYPE: TypeForm = { name: '', status: '' };

/** "Ticket Type": the form on top, every type below. */
export function TicketTypeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const notify = useNotify();
  const types = useTicketTypes(open);
  const createType = useCreateTicketType();
  const updateType = useUpdateTicketType();
  const [editing, setEditing] = useState<TicketTypeDto | null>(null);
  const { control, handleSubmit, reset, setError } = useForm<TypeForm>({
    defaultValues: EMPTY_TYPE,
    resolver: withRequiredMessages<TypeForm>(
      zodResolver(ticketTypeInputSchema) as unknown as Resolver<TypeForm>,
      { status: 'Select Status' },
    ),
  });

  useEffect(() => {
    if (open) {
      setEditing(null);
      reset(EMPTY_TYPE);
    }
  }, [open, reset]);

  const submit = handleSubmit(async (values) => {
    const input = { name: values.name, status: values.status as RecordStatus };
    try {
      if (editing) {
        await updateType.mutateAsync({ id: editing.id, input });
        notify.success('Ticket type updated.');
      } else {
        await createType.mutateAsync(input);
        notify.success('Ticket type added.');
      }
      setEditing(null);
      reset(EMPTY_TYPE);
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Ticket Type</DialogTitle>
      <DialogContent dividers>
        <Box
          component="form"
          noValidate
          onSubmit={(e) => void submit(e)}
          aria-label="Add or update a ticket type"
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr auto' },
            alignItems: 'start',
            mb: 2,
          }}
        >
          <TextInput
            control={control}
            name="name"
            label="Ticket Type"
            required
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
          <SelectInput
            control={control}
            name="status"
            label="Status"
            required
            emptyLabel="Select Status"
            options={toOptions(RECORD_STATUSES)}
          />
          <Box sx={{ display: 'flex', gap: 1, pt: 0.25 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={createType.isPending || updateType.isPending}
            >
              {editing ? 'Update Ticket Type' : 'Add Ticket Type'}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                setEditing(null);
                reset(EMPTY_TYPE);
              }}
            >
              Clear
            </Button>
          </Box>
        </Box>
        {types.error ? (
          <ErrorState error={types.error} onRetry={() => void types.refetch()} />
        ) : (
          <TableContainer>
            <Table aria-label="Ticket types">
              <TableHead>
                <TableRow>
                  {[
                    '#',
                    'Ticket Type',
                    'Created By',
                    'Created Date',
                    'Modified By',
                    'Modified Date',
                    'Status',
                    'Action',
                  ].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(types.data ?? []).map((t, i) => (
                  <TableRow key={t.id} hover>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{t.name}</TableCell>
                    <TableCell>{t.createdBy?.loginId ?? ''}</TableCell>
                    <TableCell>{formatDate(t.createdAt)}</TableCell>
                    <TableCell>{t.updatedBy?.loginId ?? ''}</TableCell>
                    <TableCell>{formatDate(t.updatedAt)}</TableCell>
                    <TableCell>
                      <StatusChip status={t.status} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          aria-label={`Edit ticket type ${t.name}`}
                          onClick={() => {
                            setEditing(t);
                            reset({ name: t.name, status: t.status });
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ---------------------------------- Groups --------------------------------- */

interface GroupForm {
  name: string;
  memberIds: string[];
}

/** "Add Group" and "Edit Group". */
export function TicketGroupDialog(props: {
  open: boolean;
  group?: TicketGroupDto;
  onClose: () => void;
}) {
  const { open, group, onClose } = props;
  const notify = useNotify();
  const assignees = useAssignees(open);
  const createGroup = useCreateTicketGroup();
  const updateGroup = useUpdateTicketGroup();
  const { control, handleSubmit, reset, setError } = useForm<GroupForm>({
    defaultValues: { name: '', memberIds: [] },
    resolver: zodResolver(ticketGroupInputSchema),
  });

  useEffect(() => {
    if (open)
      reset(
        group
          ? { name: group.name, memberIds: group.members.map((m) => m.id) }
          : { name: '', memberIds: [] },
      );
  }, [open, group, reset]);

  const submit = handleSubmit(async (input) => {
    try {
      if (group) {
        await updateGroup.mutateAsync({ id: group.id, input });
        notify.success('Group updated.');
      } else {
        await createGroup.mutateAsync(input);
        notify.success('Group added.');
      }
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  // Members of an existing group stay listed even when the caller cannot pick them anew.
  const options = new Map<string, string>(
    (assignees.data?.users ?? []).map((u) => [u.id, u.loginId]),
  );
  for (const m of group?.members ?? []) if (!options.has(m.id)) options.set(m.id, m.loginId);

  return (
    <FormDialog
      open={open}
      title={group ? 'Edit Group' : 'Add Group'}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={createGroup.isPending || updateGroup.isPending}
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <TextInput
          control={control}
          name="name"
          label="Group Name"
          required
          autoFocus
          slotProps={{ htmlInput: { maxLength: 255 } }}
        />
        <MultiSelectInput
          control={control}
          name="memberIds"
          label="User List"
          required
          options={[...options].map(([value, label]) => ({ value, label }))}
        />
      </Box>
    </FormDialog>
  );
}

/** "View Groups": the groups, who is in each, and "Edit Group". */
export function TicketGroupsTable({ onEdit }: { onEdit: (group: TicketGroupDto) => void }) {
  const groups = useTicketGroups(true);
  const [viewing, setViewing] = useState<TicketGroupDto | null>(null);
  if (groups.error)
    return <ErrorState error={groups.error} onRetry={() => void groups.refetch()} />;
  if (!groups.data) return <LoadingState />;
  return (
    <>
      <TableContainer>
        <Table aria-label="Groups">
          <TableHead>
            <TableRow>
              {['#', 'Group name', 'View Users', 'Action'].map((h) => (
                <TableCell key={h}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.data.map((g, i) => (
              <TableRow key={g.id} hover>
                <TableCell>{i + 1}</TableCell>
                <TableCell>{g.name}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => setViewing(g)}>
                    View Users
                  </Button>
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit Group">
                    <IconButton
                      size="small"
                      aria-label={`Edit Group ${g.name}`}
                      onClick={() => onEdit(g)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {groups.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No groups have been added yet.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={Boolean(viewing)} onClose={() => setViewing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{viewing?.name}</DialogTitle>
        <DialogContent dividers>
          {(viewing?.members ?? []).map((m) => (
            <Typography key={m.id}>
              {m.loginId} ({m.name})
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewing(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
